import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import useHttp from '../../hooks/http.hook'
import ShipmentPickerModal from './components/ShipmentPickerModal'
import OrderOtpModal from './components/OrderOtpModal'
import './BookingPage.css'

const DELIVERY_PICKUP = 'pickup'
const DELIVERY_SHIPMENT = 'shipment'

function formatPrice(price) {
  const n = typeof price === 'string' ? parseFloat(price) : Number(price)
  if (!Number.isFinite(n)) return '—'
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

function BookingPage() {
  const { business, branchId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { requestWithMeta } = useHttp()

  const cartItems = useMemo(() => {
    const items = location.state?.cartItems
    return Array.isArray(items) ? items : []
  }, [location.state])

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [description, setDescription] = useState('')
  const [promoCode, setPromoCode] = useState('')
  const [optionalFieldsOpen, setOptionalFieldsOpen] = useState(false)
  const [otpOpen, setOtpOpen] = useState(false)
  const [otpSending, setOtpSending] = useState(false)
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [otpError, setOtpError] = useState(null)
  const [otpBlocked, setOtpBlocked] = useState(false)
  const [otpExpiresAt, setOtpExpiresAt] = useState(null)
  const [otpPhone, setOtpPhone] = useState('')
  const orderDraftRef = useRef(null)

  const [shipments, setShipments] = useState([])
  const [shipmentsLoading, setShipmentsLoading] = useState(false)
  const [deliveryMode, setDeliveryMode] = useState(DELIVERY_PICKUP)
  const [selectedShipment, setSelectedShipment] = useState(null)
  const [shipmentAddress, setShipmentAddress] = useState('')
  const [shipmentPickerOpen, setShipmentPickerOpen] = useState(false)

  useEffect(() => {
    if (!business || !branchId) {
      setShipments([])
      return
    }

    let cancelled = false
    setShipmentsLoading(true)
    ;(async () => {
      try {
        const { ok, data } = await requestWithMeta(
          `/business/${encodeURIComponent(business)}/branches/${encodeURIComponent(branchId)}/shipments`,
          'GET'
        )
        if (cancelled) return
        if (!ok) {
          setShipments([])
          return
        }
        setShipments(Array.isArray(data?.shipments) ? data.shipments : [])
      } catch (_) {
        if (!cancelled) setShipments([])
      } finally {
        if (!cancelled) setShipmentsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [business, branchId, requestWithMeta])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (shipments.length === 0 && deliveryMode === DELIVERY_SHIPMENT) {
      setDeliveryMode(DELIVERY_PICKUP)
      setSelectedShipment(null)
      setShipmentAddress('')
      setShipmentPickerOpen(false)
    }
  }, [shipments.length, deliveryMode])

  const showDeliveryOptions = !shipmentsLoading && shipments.length > 0
  const isShipment = showDeliveryOptions && deliveryMode === DELIVERY_SHIPMENT

  const selectedShipmentId = selectedShipment?.id ?? selectedShipment?._id ?? null
  const selectedShipmentLabel = selectedShipment
    ? `${selectedShipment.locationName || 'Доставка'} — ${formatPrice(selectedShipment.price)} сом`
    : 'Выберите зону доставки'

  const canSubmit = useMemo(() => {
    if (!business || !branchId) return false
    if (!name.trim()) return false
    if (!phone.trim()) return false
    if (!cartItems.length) return false
    if (deliveryMode === DELIVERY_SHIPMENT) {
      if (selectedShipmentId == null) return false
      if (!shipmentAddress.trim()) return false
    }
    return true
  }, [
    business,
    branchId,
    name,
    phone,
    cartItems.length,
    deliveryMode,
    selectedShipmentId,
    shipmentAddress,
  ])

  const handleDeliveryModeChange = useCallback((mode) => {
    setDeliveryMode(mode)
    if (mode === DELIVERY_PICKUP) {
      setShipmentPickerOpen(false)
      setSelectedShipment(null)
      setShipmentAddress('')
    }
  }, [])

  const buildOrderBody = useCallback(() => {
    const promoFromCart = cartItems.map((it) => it?.appliedPromoCode).find(Boolean)
    const promoCodeFinal =
      promoCode.trim() || (promoFromCart ? String(promoFromCart).trim() : '')

    const products = cartItems.map((it) => {
      const line = {
        productId: it.id,
        quantity: it.quantity,
      }
      if (it.promotionId != null && it.promotionId !== '') {
        line.promotionId = it.promotionId
      }
      if (it.fromAt && it.toAt) {
        line.fromAt = it.fromAt
        line.toAt = it.toAt
      }
      return line
    })

    const body = {
      products,
      customerName: name.trim(),
      customerPhone: phone.trim(),
      customerDescription: description.trim() || undefined,
      ...(promoCodeFinal ? { promoCode: promoCodeFinal } : {}),
    }

    if (deliveryMode === DELIVERY_SHIPMENT && selectedShipmentId != null) {
      body.shipmentId = selectedShipmentId
      body.shipmentAddress = shipmentAddress.trim()
    }

    return body
  }, [
    cartItems,
    promoCode,
    name,
    phone,
    description,
    deliveryMode,
    selectedShipmentId,
    shipmentAddress,
  ])

  const sendVerification = useCallback(async (customerPhone) => {
    setOtpSending(true)
    setOtpError(null)
    try {
      const { ok, status, data } = await requestWithMeta(
        `/business/${encodeURIComponent(business)}/branches/${encodeURIComponent(branchId)}/order/verification`,
        'POST',
        { customerPhone }
      )
      if (!ok) {
        setOtpBlocked(status === 429)
        setOtpError(data?.error || 'Не удалось отправить код в WhatsApp')
        if (status === 429) setOtpExpiresAt(null)
        return
      }
      setOtpBlocked(false)
      setOtpExpiresAt((current) => {
        const next = data?.expiresAt ?? null
        if (!next || !current) return next
        const nextMs = new Date(next).getTime()
        const currentMs = new Date(current).getTime()
        if (Number.isFinite(nextMs) && Number.isFinite(currentMs) && nextMs <= currentMs) {
          return current
        }
        return next
      })
    } catch (e) {
      setOtpError(e.message || 'Не удалось отправить код в WhatsApp')
    } finally {
      setOtpSending(false)
    }
  }, [requestWithMeta, business, branchId])

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || otpSending || otpVerifying) return
    const body = buildOrderBody()
    orderDraftRef.current = body
    const nextPhone = body.customerPhone
    setOtpPhone(nextPhone)
    setOtpError(null)
    setOtpBlocked(false)
    if (nextPhone !== otpPhone) setOtpExpiresAt(null)
    setOtpSending(true)
    setOtpOpen(true)
    await sendVerification(nextPhone)
  }, [canSubmit, otpSending, otpVerifying, buildOrderBody, sendVerification, otpPhone])

  const handleResend = useCallback(async () => {
    if (otpSending || otpVerifying || otpBlocked) return
    const body = buildOrderBody()
    orderDraftRef.current = body
    setOtpPhone(body.customerPhone)
    await sendVerification(body.customerPhone)
  }, [otpSending, otpVerifying, otpBlocked, buildOrderBody, sendVerification])

  const handleVerify = useCallback(async (otp) => {
    const draft = orderDraftRef.current
    if (!draft || otpVerifying) return

    try {
      setOtpVerifying(true)
      setOtpError(null)
      const { ok, status, data } = await requestWithMeta(
        `/business/${encodeURIComponent(business)}/branches/${encodeURIComponent(branchId)}/order`,
        'POST',
        { ...draft, otp }
      )

      if (!ok) {
        setOtpBlocked(status === 429)
        setOtpError(data?.error || data?.message || 'Не удалось создать заказ')
        return
      }

      const orderId = data?.order?.id
      if (orderId != null && business) {
        // Clear cart so a successful order cannot be accidentally re-submitted.
        try {
          sessionStorage.removeItem(`shop-cart-v1-${business}`)
        } catch (_) {}
        navigate(`/${encodeURIComponent(business)}/orders/${encodeURIComponent(orderId)}`, { replace: true })
        return
      }

      setOtpError(data?.message || 'Заказ создан, но номер заказа отсутствует')
    } catch (e) {
      setOtpError(e.message || 'Не удалось создать заказ')
    } finally {
      setOtpVerifying(false)
    }
  }, [otpVerifying, requestWithMeta, business, branchId, navigate])

  const goToBusiness = useCallback(() => {
    if (business) {
      navigate(`/${encodeURIComponent(business)}`)
      return
    }
    navigate('/')
  }, [navigate, business])

  return (
    <div className="client-booking-page">
      <div className="client-booking-header">
        <div className="client-booking-title">Создать заказ</div>
      </div>

      <div className="client-booking-content">
        <div className="client-booking-column">
        <div className="client-booking-card">
          <div className="client-booking-cardTitle">Детали заказа</div>

          {!cartItems.length && (
            <div className="client-booking-warning">
              Корзина пуста. Пожалуйста, вернитесь и выберите продукты сначала.
            </div>
          )}

          <label className="client-booking-label">
            Имя
            <input
              className="client-booking-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ваше имя"
              autoComplete="name"
            />
          </label>

          <label className="client-booking-label">
            WhatsApp номер
            <input
              className="client-booking-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+996 (555) 123-456"
              autoComplete="tel"
              inputMode="tel"
            />
          </label>

          {showDeliveryOptions && (
            <div className="client-booking-delivery" role="radiogroup" aria-label="Способ получения">
              {/* <div className="client-booking-deliveryTitle">Способ получения</div> */}
              <div className="client-booking-deliveryOptions">
                <button
                  type="button"
                  className={`client-booking-deliveryOption${deliveryMode === DELIVERY_PICKUP ? ' client-booking-deliveryOption--active' : ''}`}
                  role="radio"
                  aria-checked={deliveryMode === DELIVERY_PICKUP}
                  onClick={() => handleDeliveryModeChange(DELIVERY_PICKUP)}
                >
                  Самовывоз
                </button>
                <button
                  type="button"
                  className={`client-booking-deliveryOption${deliveryMode === DELIVERY_SHIPMENT ? ' client-booking-deliveryOption--active' : ''}`}
                  role="radio"
                  aria-checked={deliveryMode === DELIVERY_SHIPMENT}
                  onClick={() => handleDeliveryModeChange(DELIVERY_SHIPMENT)}
                >
                  Доставка
                </button>
              </div>

              {isShipment && (
                <div className="client-booking-shipmentFields">
                  <label className="client-booking-label">
                    Зона доставки
                    <button
                      type="button"
                      className={`client-booking-shipmentSelect${selectedShipment ? '' : ' client-booking-shipmentSelect--placeholder'}`}
                      onClick={() => setShipmentPickerOpen(true)}
                    >
                      <span className="client-booking-shipmentSelectText">{selectedShipmentLabel}</span>
                      <svg
                        className="client-booking-shipmentSelectChevron"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </label>

                  <label className="client-booking-label">
                    Адрес доставки
                    <input
                      className="client-booking-input"
                      value={shipmentAddress}
                      onChange={(e) => setShipmentAddress(e.target.value)}
                      placeholder="Улица, дом, квартира"
                      autoComplete="street-address"
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="client-booking-optionalToggle"
            onClick={() => setOptionalFieldsOpen((open) => !open)}
            aria-expanded={optionalFieldsOpen}
            aria-label={optionalFieldsOpen ? 'Скрыть дополнительные поля' : 'Показать комментарий и промокод'}
          >
            <span className="client-booking-optionalToggleLine" aria-hidden="true" />
            <span className="client-booking-optionalToggleLabel">Еще</span>
            <svg
              className={`client-booking-optionalToggleIcon${optionalFieldsOpen ? ' client-booking-optionalToggleIcon--open' : ''}`}
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {optionalFieldsOpen && (
            <>
              <label className="client-booking-label">
                Комментарий (необязательно)
                <textarea
                  className="client-booking-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onFocus={(e) => {
                    e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' })
                  }}
                  placeholder="Что-нибудь, что мы должны знать?"
                  rows={3}
                />
              </label>

              <label className="client-booking-label">
                Промокод (необязательно)
                <input
                  className="client-booking-input"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Введите промокод"
                  autoComplete="off"
                  inputMode="text"
                />
              </label>
            </>
          )}

          <button
            className="client-booking-primary"
            type="button"
            disabled={!canSubmit || otpOpen || otpSending || otpVerifying}
            onClick={handleSubmit}
          >
            Заказать
          </button>

          <p className="client-booking-consent">
            Нажимая «Заказать», вы соглашаетесь с{' '}
            <Link to="/pages/terms">Условиями оплаты и возврата</Link> и{' '}
            <Link to="/pages/privacy">Политикой конфиденциальности</Link>.
          </p>
        </div>

        <button type="button" className="client-booking-backToShop" onClick={goToBusiness}>
          <svg
            className="client-booking-backToShopIcon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Назад в каталог
        </button>
        </div>
      </div>

      <ShipmentPickerModal
        isOpen={shipmentPickerOpen}
        shipments={shipments}
        selectedId={selectedShipmentId}
        onSelect={setSelectedShipment}
        onClose={() => setShipmentPickerOpen(false)}
      />

      <OrderOtpModal
        isOpen={otpOpen}
        phone={otpPhone}
        sending={otpSending}
        verifying={otpVerifying}
        blocked={otpBlocked}
        error={otpError}
        expiresAt={otpExpiresAt}
        onClose={() => setOtpOpen(false)}
        onResend={handleResend}
        onVerify={handleVerify}
      />
    </div>
  )
}

export default BookingPage
